import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm } from "../note-components";
import type { UnitNotes } from "../types";

const statisticsUnit9Notes: UnitNotes = {
  unitId: "unit-9",
  unitName: "Unit 9: Inference for Quantitative Data (Slopes)",
  examWeight: "2–5%",
  focus: "Regression Inference",
  sections: [
    {
      id: "unit-9-overview",
      title: "Overview",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            In previous units, you explored bivariate quantitative data using scatterplots, correlation (<InlineMath math="r" />), and the least-squares regression line (<InlineMath math="\hat{y} = a + bx" />). In Unit 9, we move from descriptive statistics to <strong>inferential statistics</strong>. We acknowledge that the slope (<InlineMath math="b" />) calculated from a sample is just an estimate of the true population slope (<InlineMath math="\beta" />). We use confidence intervals and significance tests to determine if there is a statistically significant linear relationship between two variables.
          </p>
        </div>
      )
    },
    {
      id: "topic-9-1",
      title: "Topic 9.1: Introducing Statistics: Do Those Points Align?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Concept of Sampling Variation in Slope</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When we collect bivariate data (an <InlineMath math="x" /> variable and a <InlineMath math="y" /> variable) from a sample, we can calculate a regression line. However, if we took a <em>different</em> random sample from the same population, we would likely get different data points, and therefore a slightly different regression line with a different slope (<InlineMath math="b" />).
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Sample Slope (<InlineMath math="b" />):</strong> The slope calculated from the sample data. It is a <strong>statistic</strong>.</li>
              <li><strong>Population Slope (<InlineMath math="\beta" />):</strong> The true slope of the regression line for the entire population. It is a <strong>parameter</strong>.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Sampling Distribution of <InlineMath math="b" /></h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Imagine taking every possible sample of size <InlineMath math="n" /> from a population and calculating the slope (<InlineMath math="b" />) for each. The distribution of these <InlineMath math="b" /> values is the <strong>sampling distribution of the slope</strong>.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Center:</strong> If the conditions (LINER) are met, the mean of the sampling distribution is equal to the true population slope (<InlineMath math="\mu_b = \beta" />). This means <InlineMath math="b" /> is an unbiased estimator of <InlineMath math="\beta" />.</li>
              <li><strong>Spread:</strong> The variability depends on the sample size (<InlineMath math="n" />) and the variability of the residuals (<InlineMath math="s" />). Larger sample sizes result in less variability in the slope.</li>
              <li><strong>Shape:</strong> If the responses (<InlineMath math="y" />) vary normally around the true regression line, the sampling distribution of <InlineMath math="b" /> is approximately <strong>Normal</strong>.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-2",
      title: "Topic 9.2: Confidence Intervals for the Slope of a Regression Model",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            To estimate the true population slope (<InlineMath math="\beta" />), we construct a confidence interval.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Conditions (LINER)</h3>
            <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-500">
              <ul className="space-y-2 text-slate-700">
                <li><strong>L - Linear:</strong> The relationship between <InlineMath math="x" /> and <InlineMath math="y" /> is linear. Check scatterplot and <strong>residual plot</strong> (no curved pattern).</li>
                <li><strong>I - Independent:</strong> Observations must be independent. 10% condition (<InlineMath math="n < 10\%" /> of population) if sampling without replacement.</li>
                <li><strong>N - Normal:</strong> The responses (<InlineMath math="y" />) vary normally around the regression line. Check histogram or Normal Probability Plot of <strong>residuals</strong> (no strong skew/outliers).</li>
                <li><strong>E - Equal Variance (Homoscedasticity):</strong> The standard deviation of <InlineMath math="y" /> is the same for all values of <InlineMath math="x" />. Check <strong>residual plot</strong> for consistent scatter (no "fanning" or megaphone pattern).</li>
                <li><strong>R - Random:</strong> Data is from a random sample or randomized experiment.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The generic formula for a confidence interval:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="b \pm t^* \times SE_b" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="b" />: The sample slope (often found in computer output under "Coef")</li>
              <li><InlineMath math="t^*" />: The critical value based on the confidence level and degrees of freedom</li>
              <li><InlineMath math="SE_b" />: The standard error of the slope (found in computer output under "SE Coef")</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Degrees of Freedom</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              For regression inference:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="df = n - 2" />
              <p className="text-sm text-slate-600 mt-2">(We subtract 2 because we are estimating two parameters: the slope <InlineMath math="\beta" /> and the y-intercept <InlineMath math="\alpha" />)</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Reading Computer Output</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              AP Statistics exams rarely ask you to calculate <InlineMath math="b" /> or <InlineMath math="SE_b" /> by hand. You must read standard computer output:
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border-collapse border border-slate-300 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left">Predictor</th>
                    <th className="border border-slate-300 p-2 text-left">Coef</th>
                    <th className="border border-slate-300 p-2 text-left">SE Coef</th>
                    <th className="border border-slate-300 p-2 text-left">T</th>
                    <th className="border border-slate-300 p-2 text-left">P</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2">Constant</td>
                    <td className="border border-slate-300 p-2">12.4</td>
                    <td className="border border-slate-300 p-2">1.2</td>
                    <td className="border border-slate-300 p-2">10.33</td>
                    <td className="border border-slate-300 p-2">0.000</td>
                  </tr>
                  <tr className="bg-yellow-50 font-semibold">
                    <td className="border border-slate-300 p-2">Height (x)</td>
                    <td className="border border-slate-300 p-2">0.54</td>
                    <td className="border border-slate-300 p-2">0.12</td>
                    <td className="border border-slate-300 p-2">4.50</td>
                    <td className="border border-slate-300 p-2">0.001</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Slope (<InlineMath math="b" />):</strong> 0.54 (Next to the variable name, NOT "Constant")</li>
              <li><strong>Standard Error (<InlineMath math="SE_b" />):</strong> 0.12</li>
              <li><strong>y-intercept (<InlineMath math="a" />):</strong> 12.4 (Next to "Constant")</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-3",
      title: "Topic 9.3: Justifying a Claim Based on a Confidence Interval",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Interval</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-700"><strong>Template:</strong> "We are [C]% confident that the interval from [lower bound] to [upper bound] captures the slope of the true population regression line relating [explanatory variable] and [response variable]."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpreting the Confidence Level</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm text-slate-700"><strong>Template:</strong> "If we were to take many repeated samples of the same size from this population and construct a confidence interval for the slope for each sample, approximately [C]% of those intervals would contain the true population slope."</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Justifying Claims</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Usually, we are checking if there is a relationship between the variables.
            </p>
            <ImportantNote>
              <strong>Zero (0) indicates no linear relationship</strong> — a slope of 0 means as <InlineMath math="x" /> changes, <InlineMath math="y" /> does not change linearly.
            </ImportantNote>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mt-4">
              <li><strong>If the interval does NOT contain 0:</strong> There is convincing evidence of a linear relationship between <InlineMath math="x" /> and <InlineMath math="y" />.</li>
              <li><strong>If the interval DOES contain 0:</strong> There is not convincing evidence of a linear relationship; the true slope could be 0.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-4",
      title: "Topic 9.4: Setting Up a Test for the Slope of a Regression Model",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When we want to test a specific claim about the slope (usually whether it exists), we use a significance test.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">State Hypotheses</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Null Hypothesis (<InlineMath math="H_0" />)</h4>
                <p className="text-slate-700 mt-1"><InlineMath math="\beta = 0" /></p>
                <p className="text-sm text-slate-600 mt-1"><em>Meaning:</em> There is <strong>no linear relationship</strong> between <InlineMath math="x" /> and <InlineMath math="y" /> in the population. The true slope is zero.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-900">Alternative Hypothesis (<InlineMath math="H_a" />)</h4>
                <ul className="list-disc pl-5 space-y-1 text-slate-700 mt-1">
                  <li><InlineMath math="\beta \neq 0" /> (There is a linear relationship / slope is not zero)</li>
                  <li><InlineMath math="\beta > 0" /> (There is a positive linear relationship)</li>
                  <li><InlineMath math="\beta < 0" /> (There is a negative linear relationship)</li>
                </ul>
              </div>
            </div>
            <ImportantNote>
              Always define <InlineMath math="\beta" /> in context. E.g., "<InlineMath math="\beta" /> is the true slope of the regression line relating height (<InlineMath math="x" />) to shoe size (<InlineMath math="y" />)."
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Check Conditions</h3>
            <p className="text-slate-700 leading-relaxed">
              Verify the <strong>LINER</strong> conditions (Linear, Independent, Normal Residuals, Equal Variance, Random) as detailed in Topic 9.2.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-5",
      title: "Topic 9.5: Carrying Out a Test for the Slope of a Regression Model",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculate the Test Statistic (<InlineMath math="t" />)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="t = \frac{b - \beta_0}{SE_b}" />
              <p className="text-sm text-slate-600 mt-2">Since <InlineMath math="H_0" /> usually assumes <InlineMath math="\beta_0 = 0" />, the formula simplifies to:</p>
              <BlockMath math="t = \frac{b}{SE_b}" />
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-800 mb-2">Example using computer output from Topic 9.2:</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li><InlineMath math="b = 0.54" /></li>
                <li><InlineMath math="SE_b = 0.12" /></li>
                <li><InlineMath math="t = \frac{0.54}{0.12} = 4.50" /></li>
                <li><em>Note: The computer output usually calculates this for you under the "T" column.</em></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determine the P-Value</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Use the <InlineMath math="t" />-distribution table or technology (calculator <code>tcdf</code>).</li>
              <li><strong>Degrees of Freedom:</strong> <InlineMath math="df = n - 2" /></li>
            </ul>
            <ImportantNote>
              <strong>Computer Output Warning:</strong> The P-value listed in computer output is almost always for a <strong>two-sided</strong> test (<InlineMath math="\neq" />).
              <ul className="list-disc pl-5 mt-2">
                <li>If your <InlineMath math="H_a" /> is <InlineMath math="\neq" />, use the P-value from the table directly.</li>
                <li>If your <InlineMath math="H_a" /> is <InlineMath math=">" /> or <InlineMath math="<" />, divide the computer P-value by 2 (provided the <InlineMath math="t" />-statistic sign matches your direction).</li>
              </ul>
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conclusion</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>P-value <InlineMath math="< \alpha" />:</strong> Reject <InlineMath math="H_0" />. There is convincing evidence of a linear relationship between [variable <InlineMath math="x" />] and [variable <InlineMath math="y" />].</li>
              <li><strong>P-value <InlineMath math="> \alpha" />:</strong> Fail to reject <InlineMath math="H_0" />. There is not convincing evidence of a linear relationship.</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-9-6",
      title: "Topic 9.6: Skills Focus: Selecting an Appropriate Inference Procedure",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This topic bridges Unit 9 with previous units (Unit 6: Proportions, Unit 7: Means, Unit 8: Chi-Square). You must be able to look at a scenario and decide which test is required.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Decision Flowchart</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-3">1. What type of data is it?</h4>
              
              <div className="ml-4 space-y-4">
                <div className="bg-purple-50 p-3 rounded border border-purple-200">
                  <h5 className="font-semibold text-purple-900">Categorical:</h5>
                  <ul className="list-disc pl-5 text-sm text-slate-700 mt-1">
                    <li>One sample, one variable? → 1-prop <InlineMath math="z" />-test/interval</li>
                    <li>Two samples, one variable? → 2-prop <InlineMath math="z" />-test/interval</li>
                    <li>Distribution of counts/Multiple categories? → Chi-Square (<InlineMath math="\chi^2" />) tests</li>
                  </ul>
                </div>
                
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <h5 className="font-semibold text-amber-900">Quantitative:</h5>
                  <ul className="list-disc pl-5 text-sm text-slate-700 mt-1">
                    <li>One sample, one variable (means)? → 1-sample <InlineMath math="t" />-test/interval</li>
                    <li>Two independent samples (means)? → 2-sample <InlineMath math="t" />-test/interval</li>
                    <li>Matched pairs (differences)? → Matched pairs <InlineMath math="t" />-test/interval</li>
                    <li><strong>Two quantitative variables (looking for relationship/prediction)?</strong> → <strong><InlineMath math="t" />-test/interval for Slope (Unit 9)</strong></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Identifiers for Unit 9 Problems</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>The problem mentions "slope," "regression," "linear relationship," or "predict"</li>
              <li>Data is presented in a scatterplot</li>
              <li>You are given a "Model Summary" or "Coefficients" table (Computer Output)</li>
              <li>The residuals are mentioned or plotted</li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "critical-concepts",
      title: "Critical Concepts & Common Pitfalls",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Standard Deviation of Residuals (<InlineMath math="s" />) vs. Standard Error of Slope (<InlineMath math="SE_b" />)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Computer outputs usually list a value <InlineMath math="s" /> (sometimes labeled "SE of regression").
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong><InlineMath math="s" />:</strong> The standard deviation of the residuals. It measures the typical distance actual points fall from the regression line (the "average prediction error").</li>
              <li><strong><InlineMath math="SE_b" />:</strong> The standard deviation of the sampling distribution of the slope. It measures how much the estimated slope (<InlineMath math="b" />) typically varies from the true slope (<InlineMath math="\beta" />) in repeated samples.</li>
            </ul>
            <ImportantNote>
              Do not confuse these two! For confidence intervals and <InlineMath math="t" />-tests on slope, use <strong><InlineMath math="SE_b" /></strong>.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4"><InlineMath math="r^2" /> (Coefficient of Determination)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Often found in the computer output.
            </p>
            <KeyTerm 
              term="r² (Coefficient of Determination)" 
              definition="The percent of variation in the response variable (y) that is accounted for by the linear model with the explanatory variable (x)."
            />
            <p className="text-slate-700 mt-4">
              It does <em>not</em> directly tell you if the slope is significant, but a low <InlineMath math="r^2" /> usually accompanies a non-significant slope unless <InlineMath math="n" /> is very large.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Correlation vs. Slope Significance</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>It is possible to have a very weak correlation (<InlineMath math="r" />) but a statistically significant slope if the sample size (<InlineMath math="n" />) is huge.</li>
              <li>Conversely, you can have a strong correlation that is not statistically significant if the sample size is very small (<InlineMath math="n=3" /> or <InlineMath math="4" />).</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Calculator Functions</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>LinRegTTest:</strong> Performs the significance test and calculates the P-value.</li>
              <li><strong>LinRegTInt:</strong> Calculates the confidence interval for the slope.</li>
            </ul>
            <ImportantNote>
              Both functions require raw data lists. If given computer output, you must do the math manually using the formulas <InlineMath math="b \pm t^* SE_b" /> or <InlineMath math="t = b/SE_b" />.
            </ImportantNote>
          </section>
        </div>
      )
    }
  ]
};

export default statisticsUnit9Notes;
